import { useTranslation } from 'react-i18next';
import { LANGUAGE_NAMES } from 'i18n';
import useLanguage from 'hooks/useLanguage';

const CODES = { id: 'ID', en: 'EN' };

/**
 * Compact "ID | EN" switch for the interface language. Two toggle buttons in
 * one group: the pressed one is the active language. Each button's name starts
 * with its visible code and adds the language in its own words ("EN, English"),
 * so voice control ("click EN") and screen readers both match it; `lang` makes
 * screen readers pronounce it correctly whatever the page language is.
 *
 * `size="touch"` is the 44px-tall version for the mobile menu.
 */
export default function LanguageSwitch({ size = 'compact', className = '' }) {
  const { t } = useTranslation();
  const { language, languages, setLanguage } = useLanguage();
  const height = size === 'touch' ? 'min-h-[44px] min-w-[52px] text-[14px]' : 'min-h-[36px] min-w-[40px] text-[12px]';

  return (
    <div
      role="group"
      aria-label={t('language.label')}
      className={`inline-flex items-stretch overflow-hidden rounded-btn border border-ink/40 ${className}`}
    >
      {languages.map((code) => {
        const active = code === language;
        return (
          <button
            key={code}
            type="button"
            lang={code}
            aria-pressed={active}
            aria-label={`${CODES[code]}, ${LANGUAGE_NAMES[code]}`}
            title={LANGUAGE_NAMES[code]}
            onClick={() => setLanguage(code)}
            className={`inline-flex items-center justify-center px-2.5 font-semibold tracking-[0.06em] transition-colors ${height} ${
              active ? 'bg-ink text-background' : 'bg-transparent text-ink hover:bg-ink/10'
            }`}
          >
            <span aria-hidden="true">{CODES[code]}</span>
          </button>
        );
      })}
    </div>
  );
}
